import { html, View, on } from 'rune-ts';
import { each, entries, filter, head, keys, pipe } from '@fxts/core';
import klass from './MyPageTabs.module.scss';
import { MY_PAGE_TABS_MAP, type MyPageTabsOptionMap } from '../../constant';
import { font } from '../../../../../shared/typography';
import { loading, loadingEnd } from '../../../../../shared/client/global';
import {
  PostRenderOnSelectedHookError,
  PreRenderOnSelectedHookError,
  RenderOnRefreshHookError,
} from './MyPageTabsError';
import { isApp } from '../../../../../shared/app/util';

interface MyPageTabsProps {
  tabs_map: Readonly<MyPageTabsOptionMap>;
}

export class MyPageTabs extends View<MyPageTabsProps> {
  override template() {
    const tabs = Object.entries(this.data.tabs_map);
    const typography = font('16_bold', '14_medium');

    return html`
      <section class="${klass.my_page_tabs}">
        <!-- 탭 버튼 -->
        <div class="${klass.my_page_tab_buttons}">
          ${tabs.map(
            ([key, tab]) => html`
              <button class="${klass.my_page_tab_button}" data-active="${tab.selected}" data-key="${key}">
                <a class="${typography}">${html.preventEscape(tab.value)}</a>
              </button>
            `,
          )}
        </div>

        <!-- 탭 뷰 -->
        <div class="${klass.my_page_tab_view}">
          ${tabs.filter(([, tab]) => tab.selected).map(([, tab]) => tab.view)}
        </div>
      </section>
    `;
  }

  protected override onRender() {
    window.addEventListener('message', async () => {
      if (isApp()) {
        loading();
        await this.refreshHook();
        loadingEnd();
      }
    });

    window.addEventListener('pageshow', async (event) => {
      /**
       * event.persisted
       * bfcache를 관찰하는 API
       * bfcache가 존재할 경우 event.persisted: true
       */
      if (event.persisted) {
        loading();
        await this.refreshHook();
        loadingEnd();
      }
    });
  }

  @on('click', `.${klass.my_page_tab_button}`)
  async refreshTab({
    currentTarget,
  }: {
    currentTarget: {
      dataset: {
        key: keyof typeof MY_PAGE_TABS_MAP;
      };
    };
  }) {
    pipe(
      this.data.tabs_map,
      keys,
      each((key) => {
        const tab = this.data.tabs_map[key];

        if (tab) {
          tab.selected = key === currentTarget.dataset.key;
        }
      }),
    );

    const tab = this.data.tabs_map[currentTarget.dataset.key];

    try {
      await tab?.preRenderOnSelectedHook?.(tab.view);
    } catch (error) {
      if (error instanceof Error) {
        await tab?.catchOnErrorHook?.(
          new PreRenderOnSelectedHookError(error.message, currentTarget.dataset.key),
        );
      }
    }

    this.redraw();

    try {
      await tab?.postRenderOnSelectedHook?.(tab.view);
    } catch (error) {
      if (error instanceof Error) {
        await tab?.catchOnErrorHook?.(
          new PostRenderOnSelectedHookError(error.message, currentTarget.dataset.key),
        );
      }
    }
  }

  async refreshHook() {
    try {
      await Promise.all(Object.values(this.data.tabs_map).map((tab) => tab.renderOnRefreshHook?.(tab.view)));
      this.redraw();
    } catch (error) {
      const tab_map = pipe(
        this.data.tabs_map,
        entries,
        filter(([, tab]) => tab?.selected),
        head,
      );

      if (error instanceof Error && tab_map) {
        const [tab_key, tab] = tab_map;
        await tab?.catchOnErrorHook?.(new RenderOnRefreshHookError(error.message, tab_key));
      }
    }
  }
}
